/* ============================================================
   hab-home.js — HAB homepage island (Avone v4) · Tier-3
   Vanilla, no build step, IIFE.

   Tier-3 (§18) is URL-bound: each category is its own server-rendered
   page and the cat-strip pills do full navigation, so the client-side
   hero category-swap carousel is GONE. What remains:
     · cat-strip bottom-stuck: a fixed bottom-bar clone slides up once the
       strip scrolls off the top (sentinel IntersectionObserver) (§18.3)
     · .hab-reveal IntersectionObserver fade-in (reduced-motion gated)
     · hero route rider (reduced-motion gated)
   Early-exits unless a HAB homepage element is present.
   ============================================================ */
(function(){
  'use strict';
  var hero = document.querySelector('.hab-hero');
  var strip = document.querySelector('.hab-cat-strip');
  if(!hero && !strip) return; // not a HAB homepage — bail.
  var reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── preserve scroll position across a cat-strip category navigation ──
  // (a) restore: only when the previous page set the nav flag (so back/forward
  //     and bookmark loads still land at the top). Clamp to the new (possibly
  //     shorter) page's max scroll. Instant — mimics a client-side swap.
  try{
    if(sessionStorage.getItem('hab-cat-nav') === '1'){
      var savedY = parseInt(sessionStorage.getItem('hab-cat-scroll') || '0', 10) || 0;
      sessionStorage.removeItem('hab-cat-nav');
      sessionStorage.removeItem('hab-cat-scroll');
      if(savedY > 0){
        requestAnimationFrame(function(){
          var max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
          window.scrollTo({ top: Math.min(savedY, max), behavior: 'instant' });
        });
      }
    }
  }catch(e){}
  // (b) save: before any cat-strip pill (in-flow strip OR bottom clone) navigates.
  //     Capture phase + delegation so it fires before the link follows.
  document.addEventListener('click', function(e){
    var a = e.target && e.target.closest ? e.target.closest('a.pill') : null;
    if(!a || !a.closest('.hab-cat-strip')) return;
    try{
      sessionStorage.setItem('hab-cat-scroll', String(window.scrollY || window.pageYOffset || 0));
      sessionStorage.setItem('hab-cat-nav', '1');
    }catch(err){}
  }, true);

  // ── cat-strip: starts in normal flow; once it scrolls off the top a fixed
  //    bottom-bar CLONE slides up from the bottom of the viewport. The original
  //    stays in flow (→ no layout jump). Toggled by an IntersectionObserver on
  //    a sentinel placed at the strip's natural position. ──
  if(strip && document.querySelector('.hab-cat-strip-sentinel') && !document.querySelector('.hab-cat-strip-clone')){
    var sentinel = document.querySelector('.hab-cat-strip-sentinel');
    // Build the bottom-bar clone inside a .hab-home host so the scoped CSS applies.
    var host = document.createElement('div');
    host.className = 'hab-home';
    var clone = strip.cloneNode(true);
    clone.classList.add('hab-cat-strip-clone');
    clone.removeAttribute('id');
    clone.removeAttribute('aria-labelledby');
    clone.setAttribute('aria-label', 'Bike trailer categories');
    [].slice.call(clone.querySelectorAll('[id]')).forEach(function(n){ n.removeAttribute('id'); });
    host.appendChild(clone);
    // Append to <html>, NOT <body>: Avone sets `body{overflow-x:hidden}` at
    // ≤766px, which breaks position:fixed for body descendants on mobile.
    // A child of <html> keeps the fixed clone anchored to the viewport.
    (document.documentElement || document.body).appendChild(host);

    if('IntersectionObserver' in window){
      var io2 = new IntersectionObserver(function(es){
        es.forEach(function(e){
          clone.classList.toggle('on', !e.isIntersecting && e.boundingClientRect.top <= 0);
        });
      }, { threshold: 0 });
      io2.observe(sentinel);
    }
  }

  // ── slide the bottom-stuck cat-strip clone away while a mega menu is open ──
  // Wave-3 clone sits at position:fixed;bottom:0;z-index:1000 and would otherwise
  // sit on top of the mega-menu bottom edge on short viewports (1366×768). Even
  // with .hab-mm-shell.hab-mm-fullbar bumped to z-index:1002 in hab-mega.css,
  // showing two nav surfaces at once reads noisy. Toggle body.hab-mm-open when
  // a mega-menu trigger is hovered or keyboard-focused; CSS in hab-mega.css
  // slides the clone down (translateY(100%) + visibility:hidden, 200ms ease-out).
  //
  // Event delegation on #siteNav (single listener per event) so it survives any
  // dynamic re-render of nav items. mouseover is used with mouseleave on the
  // parent (mouseleave doesn't bubble, fires only when the pointer leaves the
  // whole nav — exactly what we want for cleanup). focusin/focusout mirror the
  // pattern for keyboard users tabbing through the nav.
  var siteNav = document.getElementById('siteNav');
  if(siteNav){
    var HOST = '.hab-mm-host';
    var CLASS = 'hab-mm-open';
    var body = document.body;
    siteNav.addEventListener('mouseover', function(e){
      var t = e.target;
      if(t && t.closest && t.closest(HOST)) body.classList.add(CLASS);
    });
    siteNav.addEventListener('mouseleave', function(){
      body.classList.remove(CLASS);
    });
    siteNav.addEventListener('focusin', function(e){
      var t = e.target;
      if(t && t.closest && t.closest(HOST)) body.classList.add(CLASS);
    });
    siteNav.addEventListener('focusout', function(e){
      // If focus is moving OUTSIDE #siteNav, drop the class. Focus moves within
      // the nav (e.g. tabbing between mega-menu links) leaves the class in place.
      var next = e.relatedTarget;
      if(!next || !siteNav.contains(next)) body.classList.remove(CLASS);
    });
  }

  // ── brands strip carousel (Wave 5) ──
  // The strip now supports up to 12 brand blocks. On desktop, chevrons
  // scroll the track by one card-width per click. Chevrons auto-hide
  // when the content fits or when the track is at either end. Mobile
  // uses touch scroll with scroll-snap; chevrons hidden via CSS media
  // query. Reduced-motion swaps smooth scrollBy for instant.
  (function(){
    var carousel = document.querySelector('[data-brands-carousel]');
    if(!carousel) return;
    var track = carousel.querySelector('[data-brands-track]');
    var prev = carousel.querySelector('[data-brands-prev]');
    var next = carousel.querySelector('[data-brands-next]');
    if(!track || !prev || !next) return;
    function updateNav(){
      var canScroll = track.scrollWidth > track.clientWidth + 1;
      if(!canScroll){ prev.hidden = true; next.hidden = true; return; }
      var atStart = track.scrollLeft <= 1;
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
      prev.hidden = atStart;
      next.hidden = atEnd;
    }
    function step(dir){
      var card = track.querySelector('.brand-card');
      var w = card ? card.getBoundingClientRect().width : 200;
      track.scrollBy({ left: dir * (w + 14), behavior: reduce ? 'instant' : 'smooth' });
    }
    prev.addEventListener('click', function(){ step(-1); });
    next.addEventListener('click', function(){ step(1); });
    track.addEventListener('scroll', updateNav, { passive: true });
    window.addEventListener('resize', updateNav);
    updateNav();
  })();

  // ── announce bar rotation (Wave 7a step 6) ──
  // Rotate through 2+ pips every 6s with a fade (CSS handles the opacity
  // transition on .on toggle). Single pip → nothing to rotate; skip.
  // Reduced-motion → first pip stays visible, no rotation.
  (function(){
    var track = document.querySelector('[data-announce-track].rotate');
    if(!track) return;
    var pips = track.querySelectorAll('.hab-pip');
    if(pips.length < 2 || reduce) return;
    var i = 0;
    setInterval(function(){
      pips[i].classList.remove('on');
      i = (i + 1) % pips.length;
      pips[i].classList.add('on');
    }, 6000);
  })();

  // ── reveal on scroll ──
  if('IntersectionObserver' in window && !reduce){
    var io = new IntersectionObserver(function(es){
      es.forEach(function(e){ if(e.isIntersecting){ e.target.classList.add('in'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .08 });
    document.querySelectorAll('.hab-reveal').forEach(function(el){ io.observe(el); });
  } else {
    document.querySelectorAll('.hab-reveal').forEach(function(el){ el.classList.add('in'); });
  }

  // ── hero route rider: silhouette cycles along the dotted route line ──
  // Wave 7c batch 3 — gated on IntersectionObserver. Only starts the rAF loop
  // when the hero is in viewport; stops when out. Kills a running rAF that
  // was burning main-thread cycles even when the user had scrolled past the
  // hero — a chunk of the pre-batch-3 TBT number.
  if(hero){
    var rider = document.getElementById('routeRider');
    var path = document.getElementById('routePath');
    if(rider && path && !reduce){
      var VBW = 1440, VBH = 900, LEN = path.getTotalLength(), RDUR = 17000, start = null;
      var raf = null;
      // Wave 7c batch 4 — cache hero dimensions on init + resize instead of
      // reading clientWidth/Height every frame. Reading layout properties
      // inside rAF forces sync layout each frame ("forced reflow" in
      // Lighthouse). Cached read only on resize event = no forced reflow.
      var W = hero.clientWidth, H = hero.clientHeight;
      window.addEventListener('resize', function(){
        W = hero.clientWidth; H = hero.clientHeight;
      }, { passive: true });
      var frame = function(ts){
        if(start === null) start = ts;
        var t = ((ts - start) % RDUR) / RDUR;
        var p1 = path.getPointAtLength(t * LEN), p2 = path.getPointAtLength(Math.min(LEN, t * LEN + 1.5));
        var sx = p1.x / VBW * W, sy = p1.y / VBH * H;
        var ang = Math.atan2((p2.y - p1.y) / VBH * H, (p2.x - p1.x) / VBW * W) * 180 / Math.PI;
        rider.style.transform = 'translate(-50%,-86%) translate(' + sx.toFixed(1) + 'px,' + sy.toFixed(1) + 'px) rotate(' + ang.toFixed(2) + 'deg)';
        if(!rider.classList.contains('ready')) rider.classList.add('ready');
        raf = requestAnimationFrame(frame);
      };
      if('IntersectionObserver' in window){
        var visible = false;
        var io3 = new IntersectionObserver(function(es){
          es.forEach(function(e){
            if(e.isIntersecting && !visible){
              visible = true;
              start = null;
              raf = requestAnimationFrame(frame);
            } else if(!e.isIntersecting && visible){
              visible = false;
              if(raf) cancelAnimationFrame(raf);
              raf = null;
            }
          });
        }, { threshold: 0 });
        io3.observe(hero);
      } else {
        raf = requestAnimationFrame(frame);
      }
    }
  }

  // ── Wave 7b (1) Reduced-motion hero video pause + (7) brands mobile counter ──
  if(reduce){
    var v = document.querySelector('.hab-hero-video');
    if(v){ try{ v.autoplay = false; v.pause(); v.removeAttribute('autoplay'); }catch(e){} }
  }
  (function(){
    var track = document.querySelector('[data-brands-track]');
    if(!track) return;
    var carousel = track.closest('.brand-carousel') || track.parentNode;
    if(!carousel) return;
    var counter = document.createElement('p');
    counter.className = 'brand-counter';
    counter.setAttribute('aria-live', 'polite');
    carousel.parentNode.insertBefore(counter, carousel.nextSibling);
    function update(){
      var cards = track.querySelectorAll('.brand-card');
      if(!cards.length){ counter.textContent = ''; return; }
      var cardW = cards[0].getBoundingClientRect().width + 14;
      var visible = Math.max(1, Math.round(track.clientWidth / cardW));
      var startIdx = Math.floor(track.scrollLeft / cardW) + 1;
      var endIdx = Math.min(cards.length, startIdx + visible - 1);
      counter.textContent = startIdx + '–' + endIdx + ' of ' + cards.length + ' →';
    }
    update();
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
  })();
})();

/* ══════════════════════════════════════════════════════════════
   Wave 7c — Live-preview reinit for Shopify customizer
   ──────────────────────────────────────────────────────────────
   Customizer swaps section HTML in place on any setting change.
   The main IIFE above ran on DOMContentLoaded and attached an
   IntersectionObserver to every .hab-reveal that existed AT THAT
   TIME. Newly-injected elements have no observer → they stay in
   the pre-reveal state (opacity/transform) until page reload.
   Result: merchant reports "nothing changes until I save".

   Fix: on shopify:section:load, mark every .hab-reveal inside the
   loaded section as .in immediately (skip the animation in the
   customizer preview — merchant wants to see the final state, not
   the transition).
   ══════════════════════════════════════════════════════════════ */
(function(){
  if (typeof Shopify === 'undefined' && !document.querySelector('.hab-home')) return;
  document.addEventListener('shopify:section:load', function(e){
    var wrapper = e.target;
    if (!wrapper) return;
    wrapper.querySelectorAll('.hab-reveal').forEach(function(el){ el.classList.add('in'); });
    if (wrapper.classList && wrapper.classList.contains('hab-reveal')) wrapper.classList.add('in');
    // Wave 7d — re-init carousels on section:load
    wrapper.querySelectorAll('[data-carousel]').forEach(initHorizontalCarousel);
    // Wave 7e hotfix — re-init hero direct-video fade-in on section:load
    bindHeroDirectVideoReady(wrapper);
  });
})();

/* ══════════════════════════════════════════════════════════════
   Wave 7e hotfix — Direct hero video fade-in
   ──────────────────────────────────────────────────────────────
   Iframe hero videos add .is-ready via an inline onload handler.
   Direct <video> elements (media_video upload OR direct-MP4 URL)
   need the same crossfade treatment, but media events don't bubble
   so a document-level delegated listener won't work. This function
   attaches per-element listeners for loadeddata / error / timeout.

   Sequence per video:
   - readyState >= 2 (already loaded, e.g. cached) → reveal now
   - loadeddata → reveal after 600ms hold (matches iframe timing)
   - error → hide the video element so the poster .bg stays visible
   - 8s timeout with no ready → hide (poster remains as fallback)

   Idempotent: dataset flag stops double-binding on section:load.
   See homepage-v4/docs/hero-spec.md §onload handler for the timing rationale.
   ══════════════════════════════════════════════════════════════ */
function bindHeroDirectVideoReady(root){
  root = root || document;
  var videos = root.querySelectorAll('.hab-hero-video--direct');
  videos.forEach(function(v){
    if (v.dataset.readyBound === '1') return;
    v.dataset.readyBound = '1';
    var reveal = function(){
      setTimeout(function(){ v.classList.add('is-ready'); }, 600);
    };
    var hide = function(){ v.style.display = 'none'; };
    if (v.readyState >= 2) { reveal(); return; }
    v.addEventListener('loadeddata', reveal, { once: true });
    v.addEventListener('error', hide, { once: true });
    setTimeout(function(){
      if (!v.classList.contains('is-ready') && v.style.display !== 'none') hide();
    }, 8000);
  });
}

// Initial page-load pass — pick up any hero direct video already in the DOM.
(function(){
  if (!document.querySelector('.hab-home')) return;
  bindHeroDirectVideoReady(document);
})();

/* ══════════════════════════════════════════════════════════════
   Wave 7d — Horizontal carousel helper (reviews + journal mobile)
   ──────────────────────────────────────────────────────────────
   Applied to any element with [data-carousel]. Requires child
   .carousel__track (the scroll container) and .carousel__slide
   elements inside. Generates .carousel__dots dynamically so the
   count adapts to any number of slides (reviews limit set by
   merchant, journal pulls up to `articles_to_show`).

   Uses IntersectionObserver on each slide against the track's
   viewport — active dot follows whichever slide is >=50% visible.
   Dot click smooth-scrolls the track to align that slide.

   Idempotent: safe to call twice on the same root (checks a data
   flag before re-init) so shopify:section:load can retrigger.
   ══════════════════════════════════════════════════════════════ */
function initHorizontalCarousel(root){
  if (!root || root.dataset.carouselReady === '1') return;
  var track = root.querySelector('.carousel__track');
  if (!track) return;
  var slides = track.querySelectorAll('.carousel__slide');
  if (slides.length < 2) return; // no carousel needed for 0-1 slides

  // Build dots
  var dots = root.querySelector('.carousel__dots');
  if (!dots) {
    dots = document.createElement('div');
    dots.className = 'carousel__dots';
    // Wave 27.16 · dropped aria-hidden="true" — buttons inside are focusable + have descriptive aria-label. Trapping them from screen readers hurt a11y (keyboard could still tab in). Screen reader users benefit from being able to jump to specific slides.
    track.parentNode.insertBefore(dots, track.nextSibling);
  } else {
    dots.innerHTML = '';
  }
  slides.forEach(function(slide, i){
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'carousel__dot' + (i === 0 ? ' is-active' : '');
    b.dataset.slideIndex = String(i);
    b.setAttribute('aria-label', 'Slide ' + (i + 1) + ' of ' + slides.length);
    b.addEventListener('click', function(){
      slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    });
    dots.appendChild(b);
  });

  // Track scroll position → active dot
  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function(entries){
      entries.forEach(function(entry){
        if (entry.intersectionRatio >= 0.5) {
          var idx = Array.prototype.indexOf.call(slides, entry.target);
          if (idx < 0) return;
          dots.querySelectorAll('.carousel__dot').forEach(function(d, j){
            d.classList.toggle('is-active', j === idx);
          });
        }
      });
    }, { root: track, threshold: [0.5, 0.75] });
    slides.forEach(function(slide){ io.observe(slide); });
  }

  root.dataset.carouselReady = '1';
}

// Initial load — wire up any carousels already in the DOM
(function(){
  if (!document.querySelector('.hab-home')) return;
  document.querySelectorAll('[data-carousel]').forEach(initHorizontalCarousel);
})();
